import {
    type AfterViewChecked,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    forwardRef,
    Injector,
    inject,
    input,
    type OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { map } from 'rxjs/operators';

import { CustomControlValueAccessor } from '@app/shared/base/control-value-accessor';
import { APPROVAL_WORKFLOW_STATUS, WORKFLOW_STATUS } from '@app/shared/constants/shared.constant';
import type { ApprovalWorkflowAdapter } from '@app/shared/models/workflow.model';
import { ApprovalWorkflowService } from '@app/shared/services/approval-workflow.service';

@Component({
    selector: 'cor-workflow-selection',
    templateUrl: './workflow-selection.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowSelectionComponent),
            multi: true,
        },
    ],
    imports: [FormsModule, SelectModule],
    host: {
        '(focusout)': 'onTouched()',
    },
})
export class WorkflowSelectionComponent
    extends CustomControlValueAccessor<number>
    implements OnInit, AfterViewChecked
{
    readonly currentStatus = input<WORKFLOW_STATUS>();

    readonly object = input<string>();

    protected disabled: boolean;

    protected WORKFLOW_STATUS = WORKFLOW_STATUS;

    private _value: number;

    protected readonly workflowList = signal<ApprovalWorkflowAdapter[]>([]);

    protected readonly isLoading = signal(false);

    protected control: NgControl;

    readonly service = inject(ApprovalWorkflowService);

    private readonly injector = inject(Injector);

    private readonly cdr = inject(ChangeDetectorRef);

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.control = this.injector.get(NgControl);
        this.loadWorkflows();

        // If value is set, ensure the workflow is in the list
        if (this._value) {
            this.ensureWorkflowInList(this._value);
        }
    }

    private loadWorkflows() {
        this.isLoading.set(true);
        this.service
            .getAll({
                objectTypes: this.object(),
                statuses: APPROVAL_WORKFLOW_STATUS.ACTIVE,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                this.workflowList.set(result || []);
            })
            .add(() => this.isLoading.set(false));
    }

    private ensureWorkflowInList(id: number) {
        // Check if workflow is already in the list
        if (this.workflowList().some(w => w.id === id)) {
            return;
        }
        // Load the specific workflow if not in list
        this.service
            .getById(id)
            .pipe(map(response => response.result))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                if (response) {
                    this.workflowList.update(list => [...list, response]);
                }
            });
    }

    ngAfterViewChecked() {
        this.cdr.detectChanges();
    }

    writeValue(value: WorkflowSelectionComponent['_value']) {
        this._value = value;
    }

    /**
     * This function is called when the control status changes to or from DISABLED.
     * Depending on the value, it will enable or disable the appropriate DOM element.
     * Triggers when either form or form-control is disabled
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    get value(): WorkflowSelectionComponent['_value'] {
        return this._value;
    }

    set value(newValue) {
        if (this._value !== newValue) {
            this._value = newValue;
            this.onChange(newValue);
        }
    }
}
